// PWA Utility Functions for offline support and installation

/**
 * Initialize PWA install prompt
 */
let deferredPrompt;

export function initInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent the mini-infobar from appearing
    e.preventDefault();
    // Stash the event for later use
    deferredPrompt = e;
  });

  window.addEventListener('appinstalled', () => {
    console.log('PWA was installed');
    deferredPrompt = null;
  });
}

/**
 * Trigger PWA installation prompt
 */
export function triggerInstallPrompt() {
  if (deferredPrompt) {
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      deferredPrompt = null;
    });
  }
}

/**
 * Check if PWA is already installed
 */
export function isAppInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

/**
 * Store data offline using localStorage
 */
export function saveOfflineData(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Failed to save offline data:', error);
    return false;
  }
}

/**
 * Retrieve offline data from localStorage
 */
export function getOfflineData(key) {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Failed to retrieve offline data:', error);
    return null;
  }
}

/**
 * Clear offline data
 */
export function clearOfflineData(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Failed to clear offline data:', error);
    return false;
  }
}

/**
 * Check if online
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Add online/offline listeners
 */
export function addOnlineStatusListener(onlineCallback, offlineCallback) {
  window.addEventListener('online', onlineCallback);
  window.addEventListener('offline', offlineCallback);

  // Return unsubscribe function
  return () => {
    window.removeEventListener('online', onlineCallback);
    window.removeEventListener('offline', offlineCallback);
  };
}

/**
 * Sync offline changes when back online
 */
export async function syncOfflineData(syncFunction) {
  if (isOnline()) {
    const pendingData = getOfflineData('pending-sync');
    if (pendingData) {
      try {
        await syncFunction(pendingData);
        clearOfflineData('pending-sync');
        return true;
      } catch (error) {
        console.error('Sync failed:', error);
        return false;
      }
    }
  }
  return false;
}

/**
 * Request persistent storage permission (optional)
 */
export async function requestPersistentStorage() {
  if (navigator.storage && navigator.storage.persist) {
    const persistent = await navigator.storage.persist();
    console.log('Persistent storage granted:', persistent);
    return persistent;
  }
  return false;
}

/**
 * Get storage quota and usage
 */
export async function getStorageInfo() {
  if (navigator.storage && navigator.storage.estimate) {
    const estimate = await navigator.storage.estimate();
    return {
      usage: estimate.usage,
      quota: estimate.quota,
      percentage: (estimate.usage / estimate.quota) * 100
    };
  }
  return null;
}
